'use client';

import { useState, useMemo, useCallback } from 'react';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import styles from './index.module.scss';
import LeaderboardModal from './LeaderboardModal';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';
import EfModal from '@/components/UI/Modal';
import { useReferralStore } from '@/components/Referral/ReferralProvider';
import type { IRankItem } from '@/components/Referral/index.d';
import { useGlobal } from '@/utils/useGlobal';

// 背景图配置
const RANK_BG_CONFIG = {
    1: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766547394567-4zh2w9-compressed-Frame%20128720179.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1766556198358-l1q768-compressed-Frame%20128720179.png',
    },
    2: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766546081669-69zx5j-compressed-Frame%201612707797.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1767086193706-2cgrvm-compressed-Frame%202094654830.jpg',
    },
    3: {
        pc: 'https://websiteoss.ecoflow.com/figma-uploads/1766547415210-dgpxrh-compressed-Frame%201612707796.jpg',
        pad: 'https://websiteoss.ecoflow.com/figma-uploads/1767086203558-s82bds-compressed-Frame%202094654831.jpg',
    },
} as const;

export default function Bonus() {
    const { t } = useLang('referral.rankinglist');
    const { isMobile } = useGlobal();
    const { locale } = getLocal();
    const { referralInfo } = useReferralStore();

    // 弹窗状态
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [showRule, setShowRule] = useState(false);

    // 从 store 获取排名列表并处理排序
    const displayList = useMemo(() => {
        if (!referralInfo?.rankList?.length) return [];

        const rankList = [...referralInfo.rankList];

        if (isMobile) {
            // Mobile: 按 ranking 升序 [1, 2, 3]
            return rankList.sort((a, b) => a.ranking - b.ranking);
        }

        // PC端：按 [2, 1, 3] 顺序排列，让第一名居中
        const orderMap: Record<number, number> = { 2: 0, 1: 1, 3: 2 };
        return rankList.sort((a, b) => orderMap[a.ranking] - orderMap[b.ranking]);
    }, [referralInfo?.rankList, isMobile]);

    // 获取排名文案（西班牙语地区特殊处理）
    const getTopText = useCallback(
        (ranking: number) => {
            if (['es', 'mx'].includes(locale)) {
                if (ranking === 1) return t('primero');
                if (ranking === 2) return t('segundo');
                return t('tercero');
            }
            return `${t('top')} ${ranking}`;
        },
        [locale, t],
    );

    // 获取排名样式类
    const getRankClass = (rank: number) => {
        const classMap: Record<number, string> = {
            1: 'rank-gold',
            2: 'rank-silver',
            3: 'rank-bronze',
        };
        return classMap[rank] || '';
    };

    // 如果没有排名数据，不渲染
    if (!referralInfo?.rankList?.length) {
        return null;
    }

    return (
        <div className={`${styles.Bonus} GtmModuleData-section`}>
            <div className="sh-layout">
                <div className="bonus-header">
                    <h2 className="bonus-title">{t('title')}</h2>
                    <p className="bonus-desc">
                        {t.rich('desc', {
                            btn: (chunks: string) => (
                                <span className="rules-link" onClick={() => setShowRule(true)}>
                                    {chunks}
                                </span>
                            ),
                        })}
                    </p>
                </div>

                {/* 奖品列表 */}
                <div className="bonus-list-pc">
                    {displayList.map((item: IRankItem) => (
                        <div className={`bonus-card ${getRankClass(item.ranking)} ${item.ranking === 1 ? 'top-one' : ''}`} key={item.ranking}>
                            <div className="rank-label">{getTopText(item.ranking)}</div>
                            <div className="product-img">
                                <EcoImg pc={item.url} />
                            </div>
                            <div className="product-name">{item.prizeName}</div>
                            <div className="product-desc">{item.conditionDesc}</div>

                            <div className="bonus-bg">
                                <EcoImg
                                    pc={RANK_BG_CONFIG[item.ranking as keyof typeof RANK_BG_CONFIG]?.pc}
                                    pad={RANK_BG_CONFIG[item.ranking as keyof typeof RANK_BG_CONFIG]?.pad}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="cta-wrapper">
                    <EcoButton lRadius="ellipse" lTheme="black" onClick={() => setShowLeaderboard(true)}>
                        {t('btn')}
                    </EcoButton>
                </div>
            </div>

            {/* 排行榜弹窗 */}
            <LeaderboardModal visible={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

            {/* 规则弹窗 */}
            <EfModal
                width={640}
                visible={showRule}
                open={showRule}
                className={styles.rule_modal}
                onCancel={() => setShowRule(false)}
                maskClosable={isMobile}
                centered={!isMobile}
            >
                <h5 className="title">{t('pop_rule_title')}</h5>
                <p className="desc" dangerouslySetInnerHTML={{ __html: referralInfo?.rankRuleDesc || '' }} />
            </EfModal>
        </div>
    );
}
